// PageAutodisplayViewLinkEduDesktop.jsx
// 데스크탑 전용 자동 디스플레이 링크 페이지

import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { palette } from "../../../assets/styles/Palette";
import {
  AUTODISPLAY_SELECTED_CONTENT,
  AUTODISPLAY_ACTIVE_DPI_INFO,
  AUTODISPLAY_EDU_VIEW_DESIGN_CONCEPT,
  AUTODISPLAY_EDU_VIEW_CONTENT_GENERATOR_TITLE,
  AUTODISPLAY_EDU_VIEW_CONTENT_GENERATOR_MARKDOWN,
  AUTODISPLAY_EDU_VIEW_CONTENT_GENERATOR_JSON,
  AUTODISPLAY_EDU_VIEW_CONTENTS_PURPOSE,
  AUTODISPLAY_EDU_VIEW_CONTENTS_TYPE,
  AUTODISPLAY_EDU_VIEW_CONTENTS_MEAL,
  AUTODISPLAY_EDU_VIEW_NUTRITION_MESSAGE,
  IS_DESKTOP_SCALE,
} from "../../AtomStates";
import { useAtom } from "jotai";
import { viewDetailActivateLinkEduRequest } from "../../../utils/indexedDB";
import MoleculeBackgroundSimpleWide from "../../CreateEducationContentPage/components/molecules/MoleculeBackgroundSimpleWide";
import MoleculeMenuBoard from "../../CreateEducationContentPage/components/molecules/MoleculeMenuBoard";

const PageAutodisplayViewLinkEduDesktop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [autoDisplaySelectedContent, setAutoDisplaySelectedContent] = useAtom(
    AUTODISPLAY_SELECTED_CONTENT
  );

  const [autoDisplayActiveDPIInfo, setAutoDisplayActiveDPIInfo] = useAtom(
    AUTODISPLAY_ACTIVE_DPI_INFO
  );

  // URL 파라미터와 쿼리 파라미터 모두 처리
  const { path } = useParams();
  const [viewId, setViewId] = useState(null);
  const [urlChecked, setUrlChecked] = useState(false);
  const [activateTimestamp, setActivateTimestamp] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFullscreenGuide, setShowFullscreenGuide] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showExitGuide, setShowExitGuide] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // 재시도 로직을 위한 ref
  const retryCountRef = useRef(0);
  const isRetryingRef = useRef(false);
  const maxRetries = 10;

  // 사용자 의도적 해제 추적
  const isUserIntentionalExitRef = useRef(false);
  const lastExitTimeRef = useRef(0);

  // 자동 재시도 비활성화 플래그
  const autoRetryDisabledRef = useRef(false);

  const [autoDisplayEduViewDesignConcept, setAutoDisplayEduViewDesignConcept] =
    useAtom(AUTODISPLAY_EDU_VIEW_DESIGN_CONCEPT);
  const [autoDisplayEduViewContentsType, setAutoDisplayEduViewContentsType] =
    useAtom(AUTODISPLAY_EDU_VIEW_CONTENTS_TYPE);
  const [autoDisplayEduViewContentsMeal, setAutoDisplayEduViewContentsMeal] =
    useAtom(AUTODISPLAY_EDU_VIEW_CONTENTS_MEAL);
  const [
    autoDisplayEduViewNutritionMessage,
    setAutoDisplayEduViewNutritionMessage,
  ] = useAtom(AUTODISPLAY_EDU_VIEW_NUTRITION_MESSAGE);

  const [isDesktopScale] = useAtom(IS_DESKTOP_SCALE);

  console.log("🖥️ 데스크탑 전용 컴포넌트 렌더링");

  const [
    autoDisplayEduViewContentGeneratorTitle,
    setAutoDisplayEduViewContentGeneratorTitle,
  ] = useAtom(AUTODISPLAY_EDU_VIEW_CONTENT_GENERATOR_TITLE);
  const [
    autoDisplayEduViewContentGeneratorMarkdown,
    setAutoDisplayEduViewContentGeneratorMarkdown,
  ] = useAtom(AUTODISPLAY_EDU_VIEW_CONTENT_GENERATOR_MARKDOWN);
  const [
    autoDisplayEduViewContentGeneratorJson,
    setAutoDisplayEduViewContentGeneratorJson,
  ] = useAtom(AUTODISPLAY_EDU_VIEW_CONTENT_GENERATOR_JSON);
  const [
    autoDisplayEduViewContentsPurpose,
    setAutoDisplayEduViewContentsPurpose,
  ] = useAtom(AUTODISPLAY_EDU_VIEW_CONTENTS_PURPOSE);

  // viewId 추출 (URL 파라미터 또는 쿼리 파라미터에서)
  useEffect(() => {
    let extractedViewId = null;

    // 1. URL 파라미터에서 추출 (/AutodisplayViewLink/viewId)
    if (path) {
      extractedViewId = path;
    }
    // 2. 쿼리 파라미터에서 추출 (?viewId=xxx)
    else {
      const queryParams = new URLSearchParams(location.search);
      extractedViewId = queryParams.get("viewId");
    }

    setViewId(extractedViewId);
    setUrlChecked(true);
  }, [path, location.search]);

  // 전체화면 진입 함수 (데스크탑 전용)
  const enterFullscreen = useCallback(async () => {
    try {
      console.log("🖥️ 데스크탑: 전체화면 모드 진입 시도");

      // User activation state 확인
      if (navigator.userActivation && !navigator.userActivation.hasBeenActive) {
        console.log("사용자 활성화 필요");
        setShowFullscreenGuide(true);
        return;
      }

      // 전체화면 진입 시 body 스크롤 비활성화
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      const element = document.documentElement;

      // 다양한 브라우저 API 시도
      if (element.requestFullscreen) {
        await element.requestFullscreen({ navigationUI: "hide" });
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      }

      console.log("✅ 데스크탑: 전체화면 진입 성공");
      setShowFullscreenGuide(false); // 전체화면 진입 성공시 가이드 숨김

      // 전체화면 진입 성공 시 플래그들 초기화
      isUserIntentionalExitRef.current = false;
      autoRetryDisabledRef.current = false; // 자동 재시도 다시 활성화
    } catch (error) {
      console.log("데스크탑: 전체화면 모드 진입 실패:", error);
      setShowFullscreenGuide(true); // 전체화면 진입 실패시 가이드 표시
    }
  }, []);

  // 전체화면 해제 함수
  const exitFullscreen = useCallback(async (isUserIntentional = false) => {
    try {
      if (document.fullscreenElement) {
        // 사용자 의도적 해제 플래그 설정
        if (isUserIntentional) {
          isUserIntentionalExitRef.current = true;
          lastExitTimeRef.current = Date.now();
          autoRetryDisabledRef.current = true; // 자동 재시도 비활성화
          console.log(
            "👤 데스크탑: 사용자 의도적 전체화면 해제 - 자동 재시도 비활성화"
          );
        }

        // 전체화면 해제 시 body 스크롤 복원
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";

        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (error) {
      console.log("데스크탑: 전체화면 모드 해제 실패:", error);
    }
  }, []);

  // 전체화면 재시도 로직
  const startFullscreenRetry = useCallback(async () => {
    if (autoRetryDisabledRef.current) {
      console.log("🚫 데스크탑: 자동 재시도가 비활성화되어 있음");
      return;
    }

    if (isRetryingRef.current) {
      console.log("⏸️ 데스크탑: 이미 재시도 중이므로 중복 실행 방지");
      return; // 이미 재시도 중이면 중복 실행 방지
    }

    console.log("🚀 데스크탑: 전체화면 재시도 로직 시작");
    isRetryingRef.current = true;
    retryCountRef.current = 0;

    const attemptFullscreenAggressively = async () => {
      console.log(`데스크탑: 전체화면 시도 #${retryCountRef.current + 1}`);

      // 다양한 조건에서 시도
      const attempts = [
        () => enterFullscreen(),
        () => {
          // 강제로 사용자 제스처 시뮬레이션 시도
          const event = new MouseEvent("click", {
            view: window,
            bubbles: true,
            cancelable: true,
          });
          document.dispatchEvent(event);
          return enterFullscreen();
        },
        () => {
          // 키보드 이벤트 시뮬레이션
          const event = new KeyboardEvent("keydown", {
            key: "F11",
            bubbles: true,
            cancelable: true,
          });
          document.dispatchEvent(event);
          return enterFullscreen();
        },
      ];

      for (const attempt of attempts) {
        try {
          await attempt();
          if (document.fullscreenElement) {
            console.log("데스크탑: 전체화면 성공!");
            isRetryingRef.current = false;
            return true;
          }
        } catch (error) {
          console.log("데스크탑: 시도 실패:", error);
        }
      }

      return false;
    };

    const retryLoop = async () => {
      if (
        retryCountRef.current < maxRetries &&
        !document.fullscreenElement &&
        isRetryingRef.current
      ) {
        const success = await attemptFullscreenAggressively();

        if (!success && isRetryingRef.current) {
          retryCountRef.current++;
          // 점진적으로 지연 시간 증가 (10ms, 50ms, 100ms, 200ms, ...)
          const delay = Math.min(
            10 * Math.pow(1.5, retryCountRef.current),
            1000
          );
          setTimeout(retryLoop, delay);
        }
      } else {
        isRetryingRef.current = false;
      }
    };

    await retryLoop();
  }, [enterFullscreen]);

  // 사용자 상호작용 이벤트 리스너 등록
  const setupInteractionListeners = useCallback(() => {
    const handleFirstInteraction = async (e) => {
      console.log("데스크탑: 사용자 상호작용 감지:", e.type);

      // 자동 재시도가 비활성화된 상태에서는 자동 감지 상호작용 무시
      if (autoRetryDisabledRef.current) {
        console.log(
          "🚫 데스크탑: 자동 재시도가 비활성화됨. 자동 상호작용 무시"
        );
        return;
      }

      if (!document.fullscreenElement) {
        // 사용자가 다시 상호작용했으므로 의도적 해제 플래그 초기화
        isUserIntentionalExitRef.current = false;
        await enterFullscreen();
      }
    };

    // 더 많은 이벤트 타입에 리스너 추가
    const eventTypes = [
      "click",
      "keydown",
      "touchstart",
      "mousemove",
      "mousedown",
      "touchend",
      "pointerdown",
      "focus",
      "scroll",
    ];

    const listeners = [];
    eventTypes.forEach((eventType) => {
      const listener = (e) => handleFirstInteraction(e);
      document.addEventListener(eventType, listener, {
        once: true,
        passive: true,
      });
      listeners.push({ type: eventType, listener });
    });

    return () => {
      listeners.forEach(({ type, listener }) => {
        document.removeEventListener(type, listener);
      });
    };
  }, [enterFullscreen]);

  // 사용자 상호작용 리스너 설정 및 기타 이벤트 처리
  useEffect(() => {
    console.log("🖥️ 데스크탑: 이벤트 리스너 설정");

    // Visibility API를 활용한 추가 시도 (필요시)
    const handleVisibilityChange = async () => {
      if (
        !document.hidden &&
        !document.fullscreenElement &&
        !autoRetryDisabledRef.current
      ) {
        await enterFullscreen();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // 상호작용 리스너 설정
    const cleanupInteractionListeners = setupInteractionListeners();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      cleanupInteractionListeners();
      isRetryingRef.current = false; // 컴포넌트 언마운트시 재시도 중단
    };
  }, [enterFullscreen, setupInteractionListeners]);

  // 전체화면 상태에서 사용자 입력시 전체화면 해제
  useEffect(() => {
    const handlePageClick = (e) => {
      // 전체화면 상태에서 클릭시 전체화면 해제
      if (document.fullscreenElement) {
        exitFullscreen(true); // 사용자 의도적 해제
        setShowExitGuide(false); // 해제 가이드 숨김
      } else {
        // 전체화면이 아닌 상태에서 클릭시 전체화면 진입 (가이드 표시중일때만)
        if (showFullscreenGuide) {
          console.log("👆 데스크탑: 사용자가 가이드 클릭 - 전체화면 진입 시도");

          // 플래그들 초기화
          isUserIntentionalExitRef.current = false;
          autoRetryDisabledRef.current = false; // 자동 재시도 다시 활성화

          enterFullscreen();
        }
      }
    };

    document.addEventListener("click", handlePageClick);

    return () => {
      document.removeEventListener("click", handlePageClick);
    };
  }, [showFullscreenGuide, exitFullscreen, enterFullscreen]);

  // 키보드 이벤트로 전체화면 해제/진입
  useEffect(() => {
    const handleKeyPress = (e) => {
      // F11키 감지 (전체화면 토글)
      if (e.key === "F11") {
        e.preventDefault();
        if (document.fullscreenElement) {
          exitFullscreen(true); // 사용자 의도적 해제
          setShowExitGuide(false);
        } else {
          console.log("⌨️ 데스크탑: 사용자가 F11 키 입력 - 전체화면 진입 시도");

          // 플래그들 초기화
          isUserIntentionalExitRef.current = false;
          autoRetryDisabledRef.current = false; // 자동 재시도 다시 활성화

          enterFullscreen();
        }
        return;
      }

      // 스페이스바/엔터키
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();

        if (document.fullscreenElement) {
          // 전체화면 상태에서 키 입력시 전체화면 해제
          exitFullscreen(true); // 사용자 의도적 해제
          setShowExitGuide(false); // 해제 가이드 숨김
        } else if (showFullscreenGuide) {
          console.log("⌨️ 데스크탑: 사용자가 키 입력 - 전체화면 진입 시도");

          // 플래그들 초기화
          isUserIntentionalExitRef.current = false;
          autoRetryDisabledRef.current = false; // 자동 재시도 다시 활성화

          // 전체화면이 아닌 상태에서 키 입력시 전체화면 진입 (가이드 표시중일때만)
          enterFullscreen();
        }
      }

      // ESC키로도 전체화면 해제 가능 (브라우저 기본 동작과 동일)
      if (e.key === "Escape" && document.fullscreenElement) {
        // ESC키는 브라우저 기본 동작이므로 의도적 해제로 처리
        isUserIntentionalExitRef.current = true;
        lastExitTimeRef.current = Date.now();
        console.log("👤 데스크탑: ESC키로 사용자 의도적 전체화면 해제");
        setShowExitGuide(false);
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [showFullscreenGuide, exitFullscreen, enterFullscreen]);

  // 전체화면 상태 변경 감지
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;
      const isCurrentlyFullscreen = !!fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);

      // 전체화면이 되면 성공 메시지와 해제 가이드 표시
      if (isCurrentlyFullscreen) {
        console.log("🖥️ 데스크탑: 전체화면 모드 활성화됨");
        // 전체화면 진입 시 안내창 숨김
        setShowFullscreenGuide(false);
        // 재시도 중단
        isRetryingRef.current = false;

        // 성공 메시지 표시
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 2000); // 2초 후 숨김

        // 해제 가이드 표시
        const timer = setTimeout(() => {
          setShowExitGuide(true);
          // 3초 후 자동으로 해제 가이드 숨김
          setTimeout(() => {
            setShowExitGuide(false);
          }, 3000);
        }, 2500); // 2.5초 후 표시 (성공 메시지 후)

        return () => clearTimeout(timer);
      } else {
        console.log("🖥️ 데스크탑: 전체화면 모드 해제됨");
        setShowExitGuide(false);
        setShowSuccessMessage(false);

        // 사용자 의도적 해제인지 확인
        const isUserIntentional = isUserIntentionalExitRef.current;
        const timeSinceLastExit = Date.now() - lastExitTimeRef.current;

        if (isUserIntentional && timeSinceLastExit < 1000) {
          // 사용자가 의도적으로 해제한 경우 (1초 이내)
          console.log(
            "👤 데스크탑: 사용자 의도적 해제 감지 - 자동 재시도 완전 비활성화"
          );

          // 플래그 초기화
          isUserIntentionalExitRef.current = false;

          // 가이드만 표시하고 자동 재시도는 완전히 중단
          setTimeout(() => {
            setShowFullscreenGuide(true);
          }, 2000); // 2초 후 가이드 표시

          // 자동 재시도는 하지 않음 - 사용자가 명시적으로 클릭해야 함
          console.log("🚫 데스크탑: 자동 재시도 비활성화됨. 사용자 클릭 필요.");
        } else {
          // 의도적 해제가 아닌 경우 (예: 브라우저나 시스템에 의한 해제)
          if (autoRetryDisabledRef.current) {
            console.log("🚫 데스크탑: 자동 재시도가 비활성화됨. 가이드만 표시");
            // 가이드만 표시
            setTimeout(() => {
              setShowFullscreenGuide(true);
            }, 1000);
          } else {
            console.log(
              "🔄 데스크탑: 비의도적 전체화면 해제됨, 재시도 로직 재시작"
            );

            // 잠시 후 가이드 표시
            setTimeout(() => {
              setShowFullscreenGuide(true);
            }, 1000);

            setTimeout(() => {
              startFullscreenRetry();
              setupInteractionListeners();
            }, 500);
          }
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    // 초기 상태 설정
    handleFullscreenChange();

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, [startFullscreenRetry, setupInteractionListeners]);

  // 연결 버튼 클릭 핸들러
  useEffect(() => {
    const checkActivateState = async () => {
      try {
        setIsConnecting(true);
        setShowErrorPopup(false);

        const activateTimestamp = Date.now();
        setActivateTimestamp(activateTimestamp);

        const data = {
          viewId: viewId,
          activateCode: "",
          activateLocation: "",
          activateIp: "",
          activateTimestamp: activateTimestamp,
        };

        const response = await viewDetailActivateLinkEduRequest(data);
        console.log("🚀 데스크탑: ~ checkActivateState ~ response:", response);

        setAutoDisplayEduViewDesignConcept(response.designConcept);
        setAutoDisplayEduViewContentsType(response.contentsType);
        setAutoDisplayEduViewContentsMeal(response.contentGeneratorMeal);
        setAutoDisplayEduViewNutritionMessage(response.contentNutritionMessage);

        setAutoDisplayEduViewContentGeneratorTitle(
          response.contentGeneratorTitle
        );
        setAutoDisplayEduViewContentGeneratorMarkdown(
          response.contentGeneratorMarkdown
        );
        setAutoDisplayEduViewContentGeneratorJson(
          response.contentGeneratorJson
        );
        setAutoDisplayEduViewContentsPurpose(response.contentsPurpose);
      } catch (error) {
        console.error("데스크탑: 연결 중 오류 발생:", error);

        // 서버 응답 메시지 추출
        const serverMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "연결 중 오류가 발생했습니다. 다시 시도해주세요.";

        setErrorMessage(serverMessage);
        setShowErrorPopup(true);
      } finally {
        setIsConnecting(false);
      }
    };

    if (viewId) {
      checkActivateState();
    }
  }, [viewId]);

  // 컴포넌트 언마운트 시 스크롤 복원
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  // 페이지 로드시 즉시 전체화면 시도
  useEffect(() => {
    console.log("🖥️ 데스크탑: 페이지 로드시 전체화면 시도");
    // 즉시 재시도 시작
    startFullscreenRetry();

    // Visibility API를 활용한 추가 시도
    const handleVisibilityChange = async () => {
      if (!document.hidden && !document.fullscreenElement) {
        await enterFullscreen();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // 상호작용 리스너 설정
    const cleanupInteractionListeners = setupInteractionListeners();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      cleanupInteractionListeners();
      isRetryingRef.current = false; // 컴포넌트 언마운트시 재시도 중단
    };
  }, [startFullscreenRetry, setupInteractionListeners]);

  return (
    <FullScreenContainer>
      {/* 전체화면 가이드 메시지 */}
      {showFullscreenGuide &&
        !isConnecting &&
        !showErrorPopup &&
        !isFullscreen && (
          <FullscreenGuide>
            <GuideText>🖥️ 전체화면 모드 권장 (데스크탑)</GuideText>
            <GuideSubText>
              이 콘텐츠는 전체화면에 최적화되어 있습니다
              <br />
              <strong>F11키</strong> 또는 <strong>화면 클릭</strong>으로
              전체화면 전환
            </GuideSubText>
            <GuideNote>
              * 브라우저 보안정책으로 인해 수동 전환이 필요할 수 있습니다
            </GuideNote>
          </FullscreenGuide>
        )}

      {/* 전체화면 성공 메시지 */}
      {showSuccessMessage && (
        <SuccessMessage>
          <SuccessText>✅ 전체화면 모드 활성화! (데스크탑)</SuccessText>
        </SuccessMessage>
      )}

      {/* 전체화면 해제 가이드 */}
      {isFullscreen && showExitGuide && (
        <ExitGuide>
          <ExitGuideText>
            전체화면 해제: 화면 클릭 또는 키보드 입력 (데스크탑)
          </ExitGuideText>
        </ExitGuide>
      )}

      <>
        {autoDisplayEduViewContentsType === "meal" ? (
          <ComponentWrapper isDesktopScale={isDesktopScale}>
            <ComponentContainer isDesktopScale={isDesktopScale}>
              <MoleculeMenuBoard
                backgroundImage={autoDisplayEduViewDesignConcept}
                menuData={autoDisplayEduViewContentsMeal}
                noticeText={autoDisplayEduViewNutritionMessage}
              />
            </ComponentContainer>
          </ComponentWrapper>
        ) : (
          <ComponentWrapper isDesktopScale={isDesktopScale}>
            <ComponentContainer isDesktopScale={isDesktopScale}>
              <MoleculeBackgroundSimpleWide
                contentsDesignConcept={autoDisplayEduViewDesignConcept}
                title={autoDisplayEduViewContentGeneratorTitle}
                content={autoDisplayEduViewContentGeneratorMarkdown}
              />
            </ComponentContainer>
          </ComponentWrapper>
        )}
      </>
    </FullScreenContainer>
  );
};

export default PageAutodisplayViewLinkEduDesktop;

// 스타일 컴포넌트들 (데스크탑 전용)
const FullScreenContainer = styled.div`
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  background: white;
  overflow: hidden; /* 스크롤 완전 제거 */
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
`;

// 데스크탑 전용 wrapper - 전체화면 최적화
const ComponentWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  margin: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ComponentContainer = styled.div`
  width: 3840px;
  height: 2160px;
  background: white;
  position: relative;
  overflow: visible;

  /* 뷰포트 크기에 맞게 스케일링 - 데스크탑 최적화 */
  transform-origin: center;

  ${(props) =>
    props.isDesktopScale
      ? `
        transform: scale(0.5); /* 데스크탑에서 1/2 크기로 축소 */
      `
      : `
        transform: scale(min(calc(100vw / 3840), calc(100vh / 2160)));
      `}
`;

// 전체화면 가이드 스타일 (데스크탑 전용)
const FullscreenGuide = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 20px 40px;
  border-radius: 12px;
  z-index: 10000;
  text-align: center;
  backdrop-filter: blur(10px);
  animation: fadeIn 0.5s ease-in-out;
  border: 2px solid rgba(255, 215, 0, 0.3); /* 데스크탑 구분용 골드 테두리 */

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const GuideText = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #ffd700; /* 데스크탑 구분용 골드 색상 */
`;

const GuideSubText = styled.div`
  font-size: 14px;
  font-weight: 400;
  opacity: 0.9;
  line-height: 1.4;

  strong {
    color: #ffd700;
    font-weight: 700;
  }
`;

const GuideNote = styled.div`
  font-size: 11px;
  font-weight: 300;
  opacity: 0.7;
  margin-top: 8px;
  font-style: italic;
`;

// 전체화면 해제 가이드 스타일
const ExitGuide = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 16px 32px;
  border-radius: 12px;
  z-index: 10000;
  backdrop-filter: blur(10px);
  text-align: center;
  animation: fadeInScale 0.3s ease-in-out;
  border: 2px solid rgba(255, 215, 0, 0.2); /* 데스크탑 구분용 골드 테두리 */

  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const ExitGuideText = styled.div`
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "🖥️";
    font-size: 18px;
  }
`;

// 전체화면 성공 메시지 스타일
const SuccessMessage = styled.div`
  position: fixed;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  padding: 20px 40px;
  border-radius: 16px;
  z-index: 10001;
  text-align: center;
  animation: successBounce 0.6s ease-out;
  box-shadow: 0 8px 32px rgba(76, 175, 80, 0.3);
  border: 2px solid rgba(255, 215, 0, 0.3); /* 데스크탑 구분용 골드 테두리 */

  @keyframes successBounce {
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.5);
    }
    50% {
      transform: translate(-50%, -50%) scale(1.1);
    }
    100% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const SuccessText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #ffd700; /* 데스크탑 구분용 골드 색상 */
`;
